import { deleteRequestWithBody, getRequest, postRequest, putRequest } from '../../../core/utils/api';
import { type KeycloakContext } from '../../authentication/keycloak/KeycloakProvider';
import type PermittedAdmin from '../models/permitted-admin';
import showStatusCodeError from './logging.service';

export const getAllAdminsByOfficeId = async (officeId: string, keycloak: KeycloakContext): Promise<PermittedAdmin[] | undefined> => {
  const apiResponse = await getRequest<PermittedAdmin[]>(`office-admin/all-by-office-id?OfficeId=${officeId}`, keycloak);
  if (apiResponse.error) {
    showStatusCodeError(apiResponse.status);
    return undefined;
  }

  return apiResponse.json;
};

export const createAdmin = async (officeId: string, email: string, keycloak: KeycloakContext): Promise<string | undefined> => {
  const apiResponse = await postRequest<string>('office-admin', keycloak, {
    OfficeId: officeId,
    Email: email,
  });
  if (apiResponse.error) {
    showStatusCodeError(apiResponse.status);
    return undefined;
  }

  return apiResponse.json;
};

export const deleteAdmin = async (officeId: string, email: string, keycloak: KeycloakContext): Promise<boolean> => {
  const apiResponse = await deleteRequestWithBody('office-admin', keycloak, {
    OfficeId: officeId,
    Email: email,
  });

  if (apiResponse.error) {
    showStatusCodeError(apiResponse.status);
    return false;
  }

  return true;
};

/** Who owns the office now, and who did before. */
export interface OfficeOwner {
  readonly officeId: string;
  readonly officeName: string;
  readonly ownerEmail: string;
  readonly previousOwnerEmail?: string;
}

export interface SetOfficeOwnerOutcome {
  readonly owner?: OfficeOwner;
  /** The server's own sentence. Shown as-is; every refusal here carries one. */
  readonly error?: string;
}

/**
 * Hands the office's ownership to one of its administrators.
 *
 * There is exactly one owner because there is exactly one column behind it — the previous owner
 * stops being the owner in the same write that makes the new one, so the office is never left with
 * two or none. Only the ownership moves: the previous owner keeps their administrator role, their
 * access and their bookings.
 *
 * Whyellow staff only, unlike every other action on this screen. An office administrator allowed to
 * move ownership is an office administrator allowed to take it, so the button is hidden for
 * everyone else and the server refuses them regardless.
 */
export const setOfficeOwner = async (
  officeId: string,
  email: string,
  keycloak: KeycloakContext,
): Promise<SetOfficeOwnerOutcome> => {
  const apiResponse = await putRequest<OfficeOwner>('office-admin/owner', keycloak, {
    OfficeId: officeId,
    Email: email,
  });

  if (apiResponse.error) {
    return { error: apiResponse.error };
  }

  return { owner: apiResponse.json };
};
